import { useState, useRef, useMemo, useCallback, useEffect } from 'react'

// ─────────────────────────────────────────────────────────────
//  STITCH PLAYER
//  Pro-tool stitch-by-stitch playback. Inspired by Embird Sew Simulator,
//  Wilcom Stitch Player, and Hatch's auto-scroll feature.
//
//  Features:
//    - Drag-to-scrub on timeline (or click anywhere to jump)
//    - Hover preview shows stitch # / time / color at cursor
//    - Trim/jump tick markers above timeline (spot thread-waste hot spots)
//    - Color-block sidebar list — click a color to jump to its start
//    - Frame-by-frame step (±1) plus ±100 with shift
//    - Reverse playback button
//    - Snapshot button — exports canvas at current playback as PNG
//    - Coordinate readout (current needle x,y in mm)
//
//  State is owned by the parent (EmbroideryEditor) so the canvas render
//  and auto-scroll effect can read playProgress. This component is pure
//  UI + interaction handlers.
// ─────────────────────────────────────────────────────────────

const SPEED_PRESETS = [0.5, 1, 2, 5, 10, 50, 100, 500, 1000]

export default function StitchPlayer ({
  shapes,
  previewData,
  totalStitchPoints,
  playProgress, setPlayProgress,
  isPlaying, setIsPlaying,
  playSpeed, setPlaySpeed,
  pauseOnColorChange, setPauseOnColorChange,
  autoScroll, setAutoScroll,
  reverse, setReverse,
  canvasRef,        // for snapshot export
  filename,         // for snapshot download name
}) {
  const timelineRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [hoverPct, setHoverPct] = useState(null)   // 0..1 OR null
  const [showColorList, setShowColorList] = useState(false)

  // ── Pre-compute per-shape stitch counts + color blocks ──
  const colorBlocks = useMemo(() => {
    const blocks = []
    let cum = 0
    let cur = null
    shapes.forEach((s, i) => {
      const sc = s.segments.reduce((a, sg) => a + (sg.points?.length || 0), 0)
      const idx = s.colorIndex !== undefined ? s.colorIndex : -1
      let c = s.color || '#666'
      if (previewData?.colors?.length > 0 && idx >= 0) {
        const cv = previewData.colors[idx]
        if (cv) c = typeof cv === 'string' ? cv : (cv.hex || c)
      }
      if (cur && cur.color === c) {
        cur.end = cum + sc
        cur.shapeCount += 1
        cur.stitches += sc
      } else {
        if (cur) blocks.push(cur)
        cur = { start: cum, end: cum + sc, color: c,
                shapeCount: 1, stitches: sc, colorIndex: idx,
                blockIndex: blocks.length }
      }
      cum += sc
    })
    if (cur) blocks.push(cur)
    return blocks
  }, [shapes, previewData])

  // Current stitch count + needle position
  const currentStitch = Math.floor(playProgress * totalStitchPoints)
  const totalSeconds = totalStitchPoints / 800
  const currentSeconds = currentStitch / 800
  const fmtTime = (s) => {
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return `${m}:${String(sec).padStart(2, '0')}`
  }

  // Find current needle (x, y) by walking shape stitches
  const needlePos = useMemo(() => {
    if (totalStitchPoints === 0) return null
    let cum = 0
    for (const shape of shapes) {
      for (const seg of shape.segments) {
        const pts = seg.points || []
        if (currentStitch < cum + pts.length) {
          return pts[currentStitch - cum] || null
        }
        cum += pts.length
      }
    }
    // Past end → last point
    for (let i = shapes.length - 1; i >= 0; i--) {
      const segs = shapes[i].segments
      for (let j = segs.length - 1; j >= 0; j--) {
        const pts = segs[j].points || []
        if (pts.length) return pts[pts.length - 1]
      }
    }
    return null
  }, [shapes, currentStitch, totalStitchPoints])

  // Active color block (which thread the needle is in right now)
  const activeBlockIdx = useMemo(() => {
    let i = 0
    for (let k = 0; k < colorBlocks.length; k++) {
      if (colorBlocks[k].start <= currentStitch) i = k
      else break
    }
    return i
  }, [colorBlocks, currentStitch])

  // Trim/jump positions for the timeline tick markers. Express as
  // fractional positions along the stitch sequence — closest stitch index
  // by xy distance.
  const tickMarks = useMemo(() => {
    const marks = []
    const jumps = previewData?.jumps || []
    const trims = previewData?.trims || []
    if (totalStitchPoints === 0) return marks
    // Walk all stitches to build a flat (idx → xy) lookup ONCE
    const flat = []
    shapes.forEach(s => s.segments.forEach(seg =>
      (seg.points || []).forEach(p => flat.push(p))))
    if (flat.length === 0) return marks
    const findClosest = (xy) => {
      // Binary-ish: nearest by Manhattan; cheap enough
      let best = 0, bestD = Infinity
      for (let i = 0; i < flat.length; i++) {
        const d = Math.abs(flat[i][0] - xy[0]) + Math.abs(flat[i][1] - xy[1])
        if (d < bestD) { bestD = d; best = i; if (d === 0) break }
      }
      return best
    }
    for (const j of jumps) {
      const xy = j.to || j           // some shapes use {from,to}, others raw
      if (!xy || xy.length < 2) continue
      const idx = findClosest(xy)
      marks.push({ kind: 'jump', pct: idx / totalStitchPoints })
    }
    for (const t of trims) {
      const xy = Array.isArray(t) ? t : (t.position || t)
      if (!xy || xy.length < 2) continue
      const idx = findClosest(xy)
      marks.push({ kind: 'trim', pct: idx / totalStitchPoints })
    }
    return marks
  }, [shapes, previewData, totalStitchPoints])

  // ── Timeline interaction (click + drag-to-scrub + hover preview) ──
  const pctFromEvent = useCallback((e) => {
    const rect = timelineRef.current?.getBoundingClientRect()
    if (!rect) return 0
    return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
  }, [])

  const handleTimelineDown = (e) => {
    setIsPlaying(false)
    setIsDragging(true)
    setPlayProgress(pctFromEvent(e))
    e.preventDefault()
  }
  useEffect(() => {
    if (!isDragging) return
    const onMove = (e) => setPlayProgress(pctFromEvent(e))
    const onUp = () => setIsDragging(false)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [isDragging, pctFromEvent, setPlayProgress])

  const handleTimelineHover = (e) => setHoverPct(pctFromEvent(e))
  const handleTimelineLeave = () => setHoverPct(null)

  // Hover info: stitch # at cursor, color at cursor, time at cursor
  const hoverInfo = useMemo(() => {
    if (hoverPct === null) return null
    const stitch = Math.floor(hoverPct * totalStitchPoints)
    const block = colorBlocks.find(b => stitch >= b.start && stitch < b.end)
                || colorBlocks[colorBlocks.length - 1]
    return {
      stitch,
      time: stitch / 800,
      color: block?.color || '#666',
    }
  }, [hoverPct, totalStitchPoints, colorBlocks])

  // ── Step controls — frame-by-frame, +shift = bigger ──
  const step = useCallback((delta, shifted = false) => {
    setIsPlaying(false)
    const mult = shifted ? 100 : 1
    setPlayProgress(p => Math.max(0, Math.min(1,
      p + (delta * mult) / Math.max(1, totalStitchPoints))))
  }, [totalStitchPoints, setIsPlaying, setPlayProgress])

  const jumpToBlock = useCallback((blockIdx) => {
    if (blockIdx < 0 || blockIdx >= colorBlocks.length) return
    setIsPlaying(false)
    setPlayProgress(colorBlocks[blockIdx].start / Math.max(1, totalStitchPoints))
  }, [colorBlocks, totalStitchPoints, setIsPlaying, setPlayProgress])

  // ── Snapshot — capture current canvas state as PNG ──
  const handleSnapshot = useCallback(() => {
    if (!canvasRef?.current) return
    try {
      const url = canvasRef.current.toDataURL('image/png')
      const a = document.createElement('a')
      a.href = url
      const stitchTag = currentStitch.toString().padStart(6, '0')
      const baseName = (filename || 'stitch-snapshot')
        .replace(/\.\w+$/, '')
      a.download = `${baseName}-stitch-${stitchTag}.png`
      a.click()
    } catch (e) {
      console.warn('Snapshot failed:', e)
    }
  }, [canvasRef, filename, currentStitch])

  if (totalStitchPoints === 0) return null

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-slate-900/95 border-t border-slate-700 z-20 flex flex-col">
      {/* ── TIMELINE with trim/jump tick markers + color band + playhead ── */}
      <div className="relative" style={{ paddingTop: 8 }}>
        {/* Tick marks (above the bar) */}
        {tickMarks.map((m, i) => (
          <div
            key={i}
            className="absolute top-0 w-0.5 h-2 pointer-events-none"
            style={{
              left: `${m.pct * 100}%`,
              backgroundColor: m.kind === 'jump' ? '#ef4444' : '#f59e0b',
            }}
            title={m.kind}
          />
        ))}

        {/* Color band — segments correspond to color blocks. Clicking still
            scrubs to the cursor x — the dedicated jump-to-color buttons in
            the sidebar handle the snap-to-block-start behavior. */}
        <div
          ref={timelineRef}
          onMouseDown={handleTimelineDown}
          onMouseMove={handleTimelineHover}
          onMouseLeave={handleTimelineLeave}
          className="relative h-5 flex cursor-ew-resize select-none"
        >
          {colorBlocks.map((b, i) => {
            const w = ((b.end - b.start) / totalStitchPoints) * 100
            const isActive = i === activeBlockIdx
            return (
              <div
                key={i}
                style={{ width: `${w}%`, backgroundColor: b.color }}
                className={`h-full border-r border-slate-800/50 ${
                  isActive ? 'opacity-100' : 'opacity-70'
                }`}
                title={`Color ${b.colorIndex + 1}: ${b.stitches.toLocaleString()} stitches`}
              />
            )
          })}

          {/* Hover ghost playhead */}
          {hoverPct !== null && hoverInfo && (
            <>
              <div
                className="absolute top-0 bottom-0 w-px bg-slate-300/50 pointer-events-none"
                style={{ left: `${hoverPct * 100}%` }}
              />
              <div
                className="absolute -top-7 px-1.5 py-0.5 rounded bg-slate-800 border border-slate-600 text-[10px] text-slate-200 font-mono whitespace-nowrap pointer-events-none flex items-center gap-1"
                style={{
                  left: `${hoverPct * 100}%`,
                  transform: 'translateX(-50%)',
                }}
              >
                <span
                  className="inline-block w-2 h-2 rounded-sm border border-slate-600"
                  style={{ backgroundColor: hoverInfo.color }}
                />
                {hoverInfo.stitch.toLocaleString()} · {fmtTime(hoverInfo.time)}
              </div>
            </>
          )}

          {/* Live playhead (draggable thumb) */}
          <div
            className="absolute top-0 bottom-0 z-10 pointer-events-none"
            style={{ left: `${playProgress * 100}%` }}
          >
            <div className="absolute top-0 bottom-0 w-px bg-red-500" />
            <div className="absolute -top-1 -left-1.5 w-3 h-3 bg-red-500 rounded-full ring-2 ring-slate-900" />
          </div>
        </div>
      </div>

      {/* ── TRANSPORT + READOUT ROW ── */}
      <div className="flex items-center gap-2 px-3 py-1.5">
        {/* Rewind to start */}
        <PlayerBtn title="Rewind to start (Home)"
          onClick={() => { setIsPlaying(false); setPlayProgress(0) }}>
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
            <polygon points="19,20 9,12 19,4"/><rect x="5" y="4" width="3" height="16"/>
          </svg>
        </PlayerBtn>

        {/* Step back ±1 stitch (shift = ±100) */}
        <PlayerBtn title="Step back 1 stitch (Shift = 100)"
          onClick={(e) => step(-1, e.shiftKey)}>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <polygon points="18,20 8,12 18,4"/>
          </svg>
        </PlayerBtn>

        {/* Reverse playback toggle */}
        <PlayerBtn title="Reverse playback (un-stitch)"
          active={reverse}
          onClick={() => setReverse(s => !s)}>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <polygon points="14,4 14,20 4,12"/>
          </svg>
        </PlayerBtn>

        {/* Play/Pause (big) */}
        <button
          onClick={() => {
            if (isPlaying) { setIsPlaying(false) }
            else {
              if (!reverse && playProgress >= 1.0) setPlayProgress(0)
              if (reverse && playProgress <= 0.0) setPlayProgress(1)
              setIsPlaying(true)
            }
          }}
          className="w-9 h-9 flex items-center justify-center bg-blue-500 hover:bg-blue-400 text-white rounded-full transition-colors"
          title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
        >
          {isPlaying ? (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
            </svg>
          ) : (
            <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <polygon points="5,3 19,12 5,21"/>
            </svg>
          )}
        </button>

        {/* Step forward ±1 stitch (shift = ±100) */}
        <PlayerBtn title="Step forward 1 stitch (Shift = 100)"
          onClick={(e) => step(1, e.shiftKey)}>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <polygon points="6,4 16,12 6,20"/>
          </svg>
        </PlayerBtn>

        {/* Skip to end */}
        <PlayerBtn title="Skip to end (End)"
          onClick={() => { setIsPlaying(false); setPlayProgress(1.0) }}>
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
            <polygon points="5,4 15,12 5,20"/><rect x="16" y="4" width="3" height="16"/>
          </svg>
        </PlayerBtn>

        <div className="w-px h-5 bg-slate-700 mx-1" />

        {/* Color jump prev/next */}
        <PlayerBtn title="Jump to previous color"
          onClick={() => jumpToBlock(activeBlockIdx - 1)}>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <polygon points="11,4 4,12 11,20"/><polygon points="20,4 13,12 20,20"/>
          </svg>
        </PlayerBtn>
        <PlayerBtn title="Jump to next color"
          onClick={() => jumpToBlock(activeBlockIdx + 1)}>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <polygon points="13,4 20,12 13,20"/><polygon points="4,4 11,12 4,20"/>
          </svg>
        </PlayerBtn>

        <div className="w-px h-5 bg-slate-700 mx-1" />

        {/* Counters: stitch + time + needle (x,y) */}
        <span className="text-slate-400 text-xs font-mono">
          {currentStitch.toLocaleString()} <span className="text-slate-500">/</span>{' '}
          {totalStitchPoints.toLocaleString()}
        </span>
        <span className="text-slate-500 text-[10px] font-mono">
          {fmtTime(currentSeconds)} / {fmtTime(totalSeconds)}
        </span>
        {needlePos && (
          <span className="text-slate-500 text-[10px] font-mono">
            ({needlePos[0].toFixed(1)}, {needlePos[1].toFixed(1)}mm)
          </span>
        )}

        <div className="flex-1" />

        {/* Mode toggles */}
        <ToggleBtn
          title="Pause when stitching reaches a color change"
          active={pauseOnColorChange}
          onClick={() => setPauseOnColorChange(s => !s)}>
          ⏸ on color
        </ToggleBtn>
        <ToggleBtn
          title="Auto-scroll the canvas to follow the needle position"
          active={autoScroll}
          onClick={() => setAutoScroll(s => !s)}>
          ⊕ follow
        </ToggleBtn>
        <ToggleBtn
          title="Show the per-color list (click to jump to any color)"
          active={showColorList}
          onClick={() => setShowColorList(s => !s)}>
          ☰ colors
        </ToggleBtn>

        {/* Snapshot */}
        <PlayerBtn title="Snapshot — save current view as PNG"
          onClick={handleSnapshot}>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.66-.9l.82-1.2A2 2 0 0110.07 4h3.86a2 2 0 011.66.9l.82 1.2a2 2 0 001.66.9H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
            <circle cx="12" cy="13" r="3" />
          </svg>
        </PlayerBtn>

        {/* Speed */}
        <span className="text-slate-500 text-[10px]">Speed:</span>
        <select value={playSpeed} onChange={e => setPlaySpeed(Number(e.target.value))}
          className="bg-slate-700 text-slate-300 text-[11px] rounded px-1.5 py-0.5 border border-slate-600">
          {SPEED_PRESETS.map(s => (
            <option key={s} value={s}>{s}x</option>
          ))}
        </select>
      </div>

      {/* ── COLOR LIST (collapsible) — click any block to jump to its start ── */}
      {showColorList && (
        <div className="px-3 pb-2 border-t border-slate-800 max-h-32 overflow-y-auto">
          <div className="grid grid-cols-2 gap-1 mt-2">
            {colorBlocks.map((b, i) => {
              const isActive = i === activeBlockIdx
              return (
                <button
                  key={i}
                  onClick={() => jumpToBlock(i)}
                  className={`flex items-center gap-2 px-2 py-1 rounded text-left transition-colors ${
                    isActive
                      ? 'bg-slate-700 text-white'
                      : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <span
                    className="inline-block w-3 h-3 rounded-sm border border-slate-600 flex-shrink-0"
                    style={{ backgroundColor: b.color }}
                  />
                  <span className="text-[10px] font-mono">
                    #{b.colorIndex + 1}
                  </span>
                  <span className="text-[10px] text-slate-500 truncate flex-1">
                    {b.stitches.toLocaleString()} st
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function PlayerBtn ({ children, onClick, title, active }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${
        active
          ? 'bg-blue-500/20 text-blue-300'
          : 'text-slate-400 hover:text-white hover:bg-slate-800'
      }`}
    >
      {children}
    </button>
  )
}

function ToggleBtn ({ children, onClick, title, active }) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
        active
          ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
          : 'bg-transparent text-slate-400 border-slate-700 hover:text-white'
      }`}
    >
      {children}
    </button>
  )
}
